import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { Table, Container, Button, Modal, Form, Row, Col, InputGroup } from "react-bootstrap";
import Contexts from "../../context/Contexts";
import FormasPagoEditor from "./FormasPagoEditor";
import HaciendaPickerModal from "./HaciendaPickerModal";
import * as XLSX from "xlsx";

const apiUrl = process.env.REACT_APP_API_URL;

export default function ComprobantesEgresoList() {

  const fmtMoney = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return "—";
    try {
      return num.toLocaleString("es-AR", { style: "currency", currency: "ARS" });
    } catch {
      return `$${num.toFixed(2)}`;
    }
  };


  // === Helpers ===
  const isBlank = (v) =>
    v === undefined ||
    v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (typeof v === "number" && Number.isNaN(v));

  function validarObligatorios(obj, excepciones = []) {
    const faltan = [];
    for (const [k, v] of Object.entries(obj || {})) {
      if (excepciones.includes(k)) continue;
      const esBoolean = typeof v === "boolean";
      if (!esBoolean && isBlank(v)) faltan.push(k);
    }
    return faltan;
  }

  function validarPagos(pagosNormalizados) {
    const errores = [];
    pagosNormalizados.forEach((p, idx) => {
      const missing = [];
      const usaExistente = !!p.existing_ref;

      if (!p.formapago_id) missing.push("formapago_id");

      if (!usaExistente) {
        if (!(Number(p.monto) > 0)) missing.push("monto (> 0)");
        if (!p.fecha) missing.push("fecha");
        if (p.medio === "transferencia" && !p.banco_id) missing.push("banco_id (transferencia)");
        if (p.medio === "caja" && !p.caja_id) missing.push("caja_id (pago en caja)");
        if (p.medio === "echeq") {
          if (!p.banco_id) missing.push("banco_id (eCheq)");
          if (!p.fecha_vencimiento) missing.push("fecha_vencimiento (eCheq)");
        }
        if (p.medio === "tarjeta") {
          if (!p.tipotarjeta_id) missing.push("tipotarjeta_id (tarjeta)");
          if (!p.marcatarjeta_id) missing.push("marcatarjeta_id (tarjeta)");
          if (!String(p.cupon_numero || "").trim()) missing.push("cupon_numero (tarjeta)");
        }
        if (p.medio === "ctacte") {
          if (!p.fecha_pago) missing.push("fecha_pago (ctacte)");

          // en normalización ya mapeamos la forma futura a formapago_id,
          // así que acá solo chequeamos formapago_id
        }
      }

      if (missing.length) {
        errores.push(`Pago ${idx + 1}: falta ${missing.join(", ")}`);
      }
    });
    return errores;
  }


  const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
  const toNum = (v) => Number(v || 0);

  const calcularTotalFiscal = (comp) =>
    round2(
      toNum(comp?.neto) +
      toNum(comp?.iva105) +
      toNum(comp?.iva21) +
      toNum(comp?.iva_especial) +
      toNum(comp?.percepcion_iva) +
      toNum(comp?.percepcion_ganancias) +
      toNum(comp?.percepcion_iibb) +
      toNum(comp?.impuestos_internos) +
      toNum(comp?.exento_no_gravado) -
      toNum(comp?.retencion)
    );

  const dataContext = useContext(Contexts.DataContext);
  const {
    cajaAbierta,
    categoriasEgreso,
    proveedoresTabla = [],
    formasPagoTesoreria = [],
    librosIvaTabla = [],
    ptosVentaTabla = [],
    tiposComprobanteTabla = [],
    imputacionContableTabla = [],
    empresaSeleccionada,
    empresasTabla = [],
  } = dataContext;



  // === Estado principal ===
  const [pagos, setPagos] = useState([]);
  const [ajustes, setAjustes] = useState([]);
  const [ajustesComprobante, setAjustesComprobante] = useState([]);
  const [comprobantes, setComprobantes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedComprobante, setSelectedComprobante] = useState(null);
  const [puedeEditarComprobante, setPuedeEditarComprobante] = useState(false);
  const [motivoNoEditar, setMotivoNoEditar] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pagosComprobante, setPagosComprobante] = useState([]);
  const [
    pagosProgramadosComprobante,
    setPagosProgramadosComprobante
  ] = useState([]);
  const [
    pagosProgramadosDesasociar,
    setPagosProgramadosDesasociar
  ] = useState([]);
  const [
    situacionCtaCteComprobante,
    setSituacionCtaCteComprobante
  ] = useState(null);
  const [loadingPagos, setLoadingPagos] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [creandoComprobante, setCreandoComprobante] = useState(false);
  // [REF: HAC_MODAL_STATE]
  const [showHaciendaPickerCreate, setShowHaciendaPickerCreate] = useState(false);
  const [showHaciendaPickerEdit, setShowHaciendaPickerEdit] = useState(false);
  const [hasHaciendaDisponiblesCreate, setHasHaciendaDisponiblesCreate] = useState(false);
  const [hasHaciendaDisponiblesEdit, setHasHaciendaDisponiblesEdit] = useState(false);
  const [showProveedorCreateModal, setShowProveedorCreateModal] = useState(false);
  const [loadingCrearProveedor, setLoadingCrearProveedor] = useState(false);
  const [errorCrearProveedor, setErrorCrearProveedor] = useState("");
  const [proveedoresOptions, setProveedoresOptions] = useState([]);

  const [nuevoProveedorModal, setNuevoProveedorModal] = useState({
    nombre: "",
    direccion: "",
    telefono: "",
    email: "",
    cuit: "",
    dni: "",
    imputacioncontable_id: "",
    formapago_id: "",
  });

  // === Filtros (UI restaurada/extendida) ===
  const [filtros, setFiltros] = useState({
    texto: "",
    proveedor_id: "",
    estado: "",
    formapago_id: "", // usar 'varios' para filtrar los mixtos (sin formapago_id)
    fecha_desde: "",
    fecha_hasta: "",
  });

  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(50);

  const [orden, setOrden] = useState({
    campo: "fechacomprobante",
    direccion: "desc",
  });

  const cambiarOrden = (campo) => {
    setOrden((prev) => ({
      campo,
      direccion:
        prev.campo === campo && prev.direccion === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const onFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };
  const limpiarFiltros = () =>
    setFiltros({ texto: "", proveedor_id: "", estado: "", formapago_id: "", fecha_desde: "", fecha_hasta: "" });

  const empresasById = useMemo(() => {
    const map = {};
    for (const e of empresasTabla)
      map[e.id] = e.nombre || e.razon_social || e.descripcion || `Empresa ${e.id}`;
    return map;
  }, [empresasTabla]);

  // ↓↓↓ Agregar justo debajo de empresasById
  const tiposComprobanteById = useMemo(() => {
    const map = {};
    for (const t of tiposComprobanteTabla) {
      map[t.id] = t.descripcion || `Tipo ${t.id}`;
    }
    return map;
  }, [tiposComprobanteTabla]);

  const puedeEliminarComprobante = (comp) => {
    const estado = String(comp.estadopago || "")
      .trim()
      .toLowerCase();

    if (estado === "impaga") return true;

    // Permitir eliminar comprobantes pagados íntegramente
    // con compensación, siempre que no tengan orden de pago.
    const formaPago = formasPagoTesoreria.find(
      (fp) => Number(fp.id) === Number(comp.formapago_id)
    );

    const descripcionFormaPago = String(
      formaPago?.descripcion || ""
    )
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    const esCompensacion =
      descripcionFormaPago.includes("compensacion");

    // if (
    //   estado === "pagada" &&
    //   esCompensacion &&
    //   !comp.ordenpago_id
    // ) {
    //   return true;
    // }

    if (
      estado === "pagada" &&
      esCompensacion
    ) {
      return true;
    }

    const descripcionTipo = String(
      tiposComprobanteById[comp.tipocomprobante_id] || ""
    )
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();

    const esNotaCredito =
      descripcionTipo.includes("NOTA") &&
      descripcionTipo.includes("CREDITO");

    const sinFormaPago =
      comp.formapago_id == null ||
      comp.formapago_id === "";

    const sinOrdenPago =
      comp.ordenpago_id == null ||
      comp.ordenpago_id === "";

    return esNotaCredito && sinFormaPago && sinOrdenPago;
  };

  const hoy = new Date().toISOString().split("T")[0];

  const makeInitialNuevo = () => ({
    nrocomprobante: "",

    neto: 0,
    iva105: 0,
    iva21: 0,

    iva_especial: 0,
    iva_especial_porcentaje: "",

    percepcion_iva: 0,
    percepcion_ganancias: 0,
    percepcion_iibb: 0,

    impuestos_internos: 0,
    exento_no_gravado: 0,

    total: 0,

    tipocomprobante_id: "",
    ptoventa_id: "",
    libroiva_id: "",
    proveedor_id: "",
    fechacomprobante: hoy,
    fechavencimiento: hoy,
    imputacioncontable_id: "",
    observaciones: "",
    formapago_id: "",
    estadopago: "impaga",
    conFactura: true,
    fechapago: hoy,
    montoreal: 0,
    diferenciaefectivo: 0,
    hacienda_id: "",
    letra: "",
    retencion: 0,
    empresa_id: "",
    categoriaegreso_id: "",
  });


  const resetCreateForm = () => {
    setPagos([]);
    setAjustes([]);
    setNuevoComprobante(makeInitialNuevo());
  };
  const [nuevoComprobante, setNuevoComprobante] = useState(makeInitialNuevo());

  const librosIVAFiltrados = useMemo(() => {

    return librosIvaTabla
      .filter(
        (l) =>
          Number(l.empresa_id) ===
          Number(empresaSeleccionada?.id)
      )
      .sort((a, b) => {

        const periodoA =
          Number(a.anio) * 100 +
          Number(a.mes);

        const periodoB =
          Number(b.anio) * 100 +
          Number(b.mes);

        return periodoB - periodoA;
      });

  }, [
    librosIvaTabla,
    empresaSeleccionada?.id,
  ]);

  const ptosVtaFiltrados = useMemo(() => {

    return ptosVentaTabla
      .filter(
        (l) =>
          Number(l.empresa_id) ===
          Number(empresaSeleccionada?.id)
      )
      .sort((a, b) => {

        /*
         * Ordenamos numéricamente por la descripción
         * del punto de venta.
         *
         * Ejemplo:
         * 12
         * 10
         * 5
         * 2
         */
        const numeroA =
          Number(
            String(a.descripcion || "")
              .replace(/\D/g, "")
          ) || 0;

        const numeroB =
          Number(
            String(b.descripcion || "")
              .replace(/\D/g, "")
          ) || 0;

        return numeroA - numeroB

      });

  }, [
    ptosVentaTabla,
    empresaSeleccionada?.id,
  ]);

  const obtenerLibroIVAPorFecha = useCallback(
    (fecha) => {

      if (
        !fecha ||
        !empresaSeleccionada?.id
      ) {
        return null;
      }

      const librosEmpresa =
        librosIvaTabla.filter(
          (l) =>
            Number(l.empresa_id) ===
            Number(empresaSeleccionada.id)
        );

      if (!librosEmpresa.length) {
        return null;
      }


      /*
       * La fecha viene como YYYY-MM-DD.
       *
       * Evitamos new Date() para no introducir
       * problemas de zona horaria.
       */
      const [
        anioFecha,
        mesFecha,
      ] = String(fecha)
        .split("-")
        .map(Number);


      /*
       * 1. Primero buscamos coincidencia exacta
       *    mes + año.
       */
      const exacto =
        librosEmpresa.find(
          (l) =>
            Number(l.mes) === mesFecha &&
            Number(l.anio) === anioFecha
        );

      if (exacto) {
        return exacto;
      }


      /*
       * 2. Si ese período no existe,
       *    seleccionamos el Libro IVA
       *    cronológicamente más reciente.
       */
      const ordenados =
        [...librosEmpresa].sort(
          (a, b) => {

            const periodoA =
              Number(a.anio) * 100 +
              Number(a.mes);

            const periodoB =
              Number(b.anio) * 100 +
              Number(b.mes);

            return periodoB - periodoA;
          }
        );

      return ordenados[0] || null;

    },
    [
      librosIvaTabla,
      empresaSeleccionada?.id,
    ]
  );

  const loadComprobantes = useCallback(async () => {
    try {
      let url = `${apiUrl}/comprobantes-egreso`;
      if (empresaSeleccionada?.id) url += `?empresa_id=${empresaSeleccionada.id}`;
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      setComprobantes(data.sort((a, b) => a.id - b.id));
    } catch (error) {
      console.error("❌ Error al cargar comprobantes:", error);
    }
  }, [empresaSeleccionada?.id]);

  useEffect(() => {
    loadComprobantes();
  }, [loadComprobantes]);

  useEffect(() => {
    setProveedoresOptions(Array.isArray(proveedoresTabla) ? proveedoresTabla : []);
  }, [proveedoresTabla]);

  // const checkHaciendaDisponibles = useCallback(async ({ empresaId, proveedorId }) => {
  //   try {
  //     if (!empresaId || !proveedorId) return false;
  //     const params = new URLSearchParams();
  //     params.set("empresa_id", empresaId);
  //     params.set("proveedor_id", proveedorId);
  //     params.set("limit", "1");
  //     const res = await fetch(`${apiUrl}/hacienda/disponibles?${params.toString()}`, { credentials: "include" });
  //     const data = await res.json().catch(() => []);
  //     if (!res.ok) return false;
  //     return Array.isArray(data) && data.length > 0;
  //   } catch {
  //     return false;
  //   }
  // }, []);

  const checkHaciendaDisponibles = useCallback(async ({ empresaId, proveedorId, comprobanteId }) => {
    try {
      if (!empresaId || !proveedorId) return false;
      const params = new URLSearchParams();
      params.set("empresa_id", empresaId);
      params.set("proveedor_id", proveedorId);
      if (comprobanteId) params.set("comprobante_id", comprobanteId);
      params.set("limit", "1");
      const res = await fetch(`${apiUrl}/hacienda/disponibles?${params.toString()}`, { credentials: "include" });
      const data = await res.json().catch(() => []);
      if (!res.ok) return false;
      return Array.isArray(data) && data.length > 0;
    } catch {
      return false;
    }
  }, []);

  // [REF: HAC_CREATE_AVAIL] check al abrir el modal de CREACIÓN
  useEffect(() => {
    (async () => {
      if (!showCreateModal) return;
      const prov = nuevoComprobante?.proveedor_id;
      if (empresaSeleccionada?.id && prov) {
        const ok = await checkHaciendaDisponibles({
          empresaId: empresaSeleccionada.id,
          proveedorId: prov,
        });
        setHasHaciendaDisponiblesCreate(ok);
      } else {
        setHasHaciendaDisponiblesCreate(false);
      }
    })();
  }, [showCreateModal, empresaSeleccionada?.id, nuevoComprobante?.proveedor_id, checkHaciendaDisponibles]);




  // === Comprobantes filtrados (cliente) ===
  const comprobantesFiltrados = useMemo(() => {
    let arr = [...comprobantes];

    // proveedor
    if (filtros.proveedor_id) {
      arr = arr.filter((c) => String(c.proveedor_id) === String(filtros.proveedor_id));
    }

    // estado
    if (filtros.estado) {
      arr = arr.filter((c) => (c.estadopago || "").toLowerCase() === filtros.estado.toLowerCase());
    }

    // forma de pago (incluye "varios")
    if (filtros.formapago_id) {
      if (filtros.formapago_id === "varios") {
        arr = arr.filter((c) => !c.formapago_id);
      } else {
        arr = arr.filter(
          (c) => c.formapago_id && String(c.formapago_id) === String(filtros.formapago_id)
        );
      }
    }

    // fecha (usamos fechacomprobante)
    if (filtros.fecha_desde) {
      arr = arr.filter((c) => (c.fechacomprobante || "") >= filtros.fecha_desde);
    }
    if (filtros.fecha_hasta) {
      arr = arr.filter((c) => (c.fechacomprobante || "") <= filtros.fecha_hasta);
    }

    // texto libre: nrocomprobante, letra, observaciones
    if (filtros.texto.trim() !== "") {
      const q = filtros.texto.trim().toLowerCase();
      arr = arr.filter((c) => {
        const s1 = (c.nrocomprobante || "").toLowerCase();
        const s2 = (c.letra || "").toLowerCase();
        const s3 = (c.observaciones || "").toLowerCase();
        return s1.includes(q) || s2.includes(q) || s3.includes(q);
      });
    }

    return arr;
  }, [comprobantes, filtros]);

  // Totales útiles
  const totalFiltrado = useMemo(
    () => comprobantesFiltrados.reduce((acc, c) => acc + (Number(c.total) || 0), 0),
    [comprobantesFiltrados]
  );

  const comprobantesOrdenados = useMemo(() => {
    const arr = [...comprobantesFiltrados];

    const obtenerValor = (comp, campo) => {
      switch (campo) {
        case "proveedor":
          return proveedoresTabla.find(
            (p) => Number(p.id) === Number(comp.proveedor_id)
          )?.nombre || "";

        case "tipo":
          return tiposComprobanteById[comp.tipocomprobante_id] || "";

        case "empresa":
          return empresasById[comp.empresa_id] || "";

        case "formapago":
          return formasPagoTesoreria.find(
            (fp) => Number(fp.id) === Number(comp.formapago_id)
          )?.descripcion || "";

        default:
          return comp[campo];
      }
    };

    const camposNumericos = [
      "id", "neto", "iva105", "iva21",
      "iva_especial", "total", "hacienda_id",
    ];

    arr.sort((a, b) => {
      const va = obtenerValor(a, orden.campo);
      const vb = obtenerValor(b, orden.campo);

      let resultado = 0;

      if (camposNumericos.includes(orden.campo)) {
        resultado = Number(va || 0) - Number(vb || 0);
      } else {
        resultado = String(va ?? "").localeCompare(
          String(vb ?? ""),
          "es",
          { numeric: true, sensitivity: "base" }
        );
      }

      if (resultado === 0) {
        resultado = Number(a.id) - Number(b.id);
      }

      return orden.direccion === "asc" ? resultado : -resultado;
    });

    return arr;
  }, [
    comprobantesFiltrados,
    orden,
    proveedoresTabla,
    tiposComprobanteById,
    empresasById,
    formasPagoTesoreria,
  ]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(comprobantesOrdenados.length / registrosPorPagina)
  );

  const paginaVisible = Math.min(paginaActual, totalPaginas);

  const comprobantesPaginados = useMemo(() => {
    const inicio = (paginaVisible - 1) * registrosPorPagina;

    return comprobantesOrdenados.slice(
      inicio,
      inicio + registrosPorPagina
    );
  }, [comprobantesOrdenados, paginaVisible, registrosPorPagina]);

  useEffect(() => {
    setPaginaActual(1);
  }, [filtros, registrosPorPagina, orden]);

  const exportarExcel = () => {
    const datos = comprobantesOrdenados.map((comp) => ({
      ID: comp.id,
      Comprobante: comp.nrocomprobante,
      Tipo:
        tiposComprobanteById[comp.tipocomprobante_id] ||
        comp.tipocomprobante_id,
      Fecha: comp.fechacomprobante,
      Neto: Number(comp.neto || 0),
      "IVA 10,5 %": Number(comp.iva105 || 0),
      "IVA 21 %": Number(comp.iva21 || 0),
      "IVA especial": Number(comp.iva_especial || 0),
      Total: Number(comp.total || 0),
      Proveedor:
        proveedoresTabla.find(
          (p) => Number(p.id) === Number(comp.proveedor_id)
        )?.nombre || comp.proveedor_id,
      Empresa:
        empresasById[comp.empresa_id] || comp.empresa_id,
      Hacienda: comp.hacienda_id || "",
      "Forma de Pago":
        formasPagoTesoreria.find(
          (fp) => Number(fp.id) === Number(comp.formapago_id)
        )?.descripcion || "VARIOS",
      "Estado Pago": comp.estadopago,
    }));

    const hoja = XLSX.utils.json_to_sheet(datos);

    hoja["!cols"] = [
      { wch: 10 }, // ID
      { wch: 18 }, // Comprobante
      { wch: 20 }, // Tipo
      { wch: 15 }, // Fecha
      { wch: 18 }, // Neto
      { wch: 18 }, // IVA 10,5 %
      { wch: 18 }, // IVA 21 %
      { wch: 18 }, // IVA especial
      { wch: 18 }, // Total
      { wch: 35 }, // Proveedor
      { wch: 25 }, // Empresa
      { wch: 15 }, // Hacienda
      { wch: 22 }, // Forma de Pago
      { wch: 18 }, // Estado Pago
    ];

    const libro = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      libro,
      hoja,
      "Comprobantes de Egreso"
    );

    XLSX.writeFile(
      libro,
      "Comprobantes_Egreso.xlsx"
    );
  };

  // === LCD Flags ===
  const esLCDCreate = useMemo(() => {
    const t = tiposComprobanteTabla.find(
      (tc) => String(tc.id) === String(nuevoComprobante.tipocomprobante_id)
    );
    return (t?.descripcion || "").toUpperCase() === "LCD";
  }, [tiposComprobanteTabla, nuevoComprobante.tipocomprobante_id]);

  const esLCDEdit = useMemo(() => {
    if (!selectedComprobante) return false;
    const t = tiposComprobanteTabla.find(
      (tc) => String(tc.id) === String(selectedComprobante.tipocomprobante_id)
    );
    return (t?.descripcion || "").toUpperCase() === "LCD";
  }, [tiposComprobanteTabla, selectedComprobante?.tipocomprobante_id]);

  // === Auto TOTAL fiscal (creación)
  useEffect(() => {
    setNuevoComprobante((prev) => {
      if (!prev) return prev;

      const newTotal =
        calcularTotalFiscal(prev);

      return Number(prev.total) === newTotal
        ? prev
        : {
          ...prev,
          total: newTotal,
        };
    });
  }, [
    nuevoComprobante.neto,
    nuevoComprobante.iva105,
    nuevoComprobante.iva21,
    nuevoComprobante.iva_especial,
    nuevoComprobante.percepcion_iva,
    nuevoComprobante.percepcion_ganancias,
    nuevoComprobante.percepcion_iibb,
    nuevoComprobante.impuestos_internos,
    nuevoComprobante.exento_no_gravado,
    nuevoComprobante.retencion,
  ]);

  // === Auto TOTAL fiscal (edición)
  useEffect(() => {
    if (!selectedComprobante) return;

    setSelectedComprobante((prev) => {
      if (!prev) return prev;

      const newTotal =
        calcularTotalFiscal(prev);

      return Number(prev.total) === newTotal
        ? prev
        : {
          ...prev,
          total: newTotal,
        };
    });
  }, [
    selectedComprobante?.neto,
    selectedComprobante?.iva105,
    selectedComprobante?.iva21,
    selectedComprobante?.iva_especial,
    selectedComprobante?.percepcion_iva,
    selectedComprobante?.percepcion_ganancias,
    selectedComprobante?.percepcion_iibb,
    selectedComprobante?.impuestos_internos,
    selectedComprobante?.exento_no_gravado,
    selectedComprobante?.retencion,
  ]);

  // === Diferencia Efectivo: si no es LCD → 0 y readonly; si es LCD → auto (montoreal - total)
  useEffect(() => {
    setNuevoComprobante((prev) => {
      if (!prev) return prev;
      if (!esLCDCreate) {
        if (toNum(prev.montoreal) !== 0 || toNum(prev.diferenciaefectivo) !== 0) {
          return { ...prev, montoreal: 0, diferenciaefectivo: 0 };
        }
        return prev;
      }
      const diff = round2(toNum(prev.montoreal) - toNum(prev.total));
      return prev.diferenciaefectivo === diff ? prev : { ...prev, diferenciaefectivo: diff };
    });
  }, [esLCDCreate, nuevoComprobante.montoreal, nuevoComprobante.total]);

  useEffect(() => {
    if (!selectedComprobante) return;
    setSelectedComprobante((prev) => {
      if (!prev) return prev;
      if (!esLCDEdit) {
        if (toNum(prev.montoreal) !== 0 || toNum(prev.diferenciaefectivo) !== 0) {
          return { ...prev, montoreal: 0, diferenciaefectivo: 0 };
        }
        return prev;
      }
      const diff = round2(toNum(prev.montoreal) - toNum(prev.total));
      return prev.diferenciaefectivo === diff ? prev : { ...prev, diferenciaefectivo: diff };
    });
  }, [esLCDEdit, selectedComprobante?.montoreal, selectedComprobante?.total]);

  // === IVA quick calc (creación)
  const calcIVA105Create = () => {
    const base = toNum(nuevoComprobante.neto);
    setNuevoComprobante((p) => ({ ...p, iva105: round2(base * 0.105) }));
  };
  const calcIVA21Create = () => {
    const base = toNum(nuevoComprobante.neto);
    setNuevoComprobante((p) => ({ ...p, iva21: round2(base * 0.21) }));
  };

  const calcIVAEspecialCreate = () => {
    const base =
      toNum(nuevoComprobante.neto);

    const porcentaje =
      toNum(
        nuevoComprobante.iva_especial_porcentaje
      );

    setNuevoComprobante((prev) => ({
      ...prev,

      iva_especial:
        round2(
          base * porcentaje / 100
        ),
    }));
  };

  const calcIVAEspecialEdit = () => {
    if (!selectedComprobante) return;

    const base =
      toNum(selectedComprobante.neto);

    const porcentaje =
      toNum(
        selectedComprobante.iva_especial_porcentaje
      );

    setSelectedComprobante((prev) => ({
      ...prev,

      iva_especial:
        round2(
          base * porcentaje / 100
        ),
    }));
  };

  // === IVA quick calc (edición)
  const calcIVA105Edit = () => {
    if (!selectedComprobante) return;
    const base = toNum(selectedComprobante.neto);
    setSelectedComprobante((p) => ({ ...p, iva105: round2(base * 0.105) }));
  };
  const calcIVA21Edit = () => {
    if (!selectedComprobante) return;
    const base = toNum(selectedComprobante.neto);
    setSelectedComprobante((p) => ({ ...p, iva21: round2(base * 0.21) }));
  };

  // === Edición
  const handleDoubleClick = async (comprobante) => {

    console.log("========================================");
    console.log("🟡 ABRIENDO COMPROBANTE");
    console.log("ID:", comprobante?.id);
    console.log("========================================");

    if (!empresaSeleccionada) {
      alert("Debe seleccionar una empresa.");
      return;
    }

    setLoadingPagos(true);
    setPagosComprobante([]);
    setPagosProgramadosDesasociar([]);

    try {

      const res = await fetch(
        `${apiUrl}/comprobantes-egreso/${comprobante.id}/detalle`,
        {
          credentials: "include",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error ||
          "No se pudo obtener el detalle del comprobante"
        );
      }

      console.log("🟢 DETALLE COMPROBANTE:", data);
      console.log(
        "🔵 PAGOS DEL COMPROBANTE:",
        data?.pagos
      );
      console.log("🟢 puede_editar:", data?.puede_editar);
      console.log("🟢 motivo:", data?.motivo_no_editar);

      /*
       * Guardamos si el comprobante puede editarse.
       */
      const puedeEditar =
        data?.puede_editar === true;

      setPuedeEditarComprobante(
        puedeEditar
      );

      setMotivoNoEditar(
        puedeEditar
          ? ""
          : (
            data?.motivo_no_editar ||
            "Este comprobante no puede ser editado porque tiene formas de pago efectivas o pagos programados asociados."
          )
      );

      setSelectedComprobante(
        data?.comprobante || comprobante
      );

      setPagosComprobante(
        Array.isArray(data?.pagos)
          ? data.pagos
          : []
      );

      setPagosProgramadosComprobante(
        Array.isArray(data?.pagos_programados)
          ? data.pagos_programados
          : []
      );
      setSituacionCtaCteComprobante(
        data?.ctacte || null
      );
      setAjustesComprobante(
        Array.isArray(data?.ajustes)
          ? data.ajustes
          : []
      );

      /*
       * Ahora SIEMPRE abrimos el modal.
       */
      setShowModal(true);

    } catch (err) {

      console.error(
        "❌ Error al cargar comprobante:",
        err
      );

      alert(
        err.message ||
        "No se pudo cargar el comprobante."
      );

      setSelectedComprobante(null);
      setPuedeEditarComprobante(false);
      setMotivoNoEditar("");
      setShowModal(false);

    } finally {

      setLoadingPagos(false);

    }
  };

  // [REF: HAC_EDIT_AVAIL] check si hay haciendas disponibles en EDICIÓN
  useEffect(() => {
    (async () => {
      if (!showModal || !selectedComprobante?.proveedor_id || !empresaSeleccionada?.id) {
        setHasHaciendaDisponiblesEdit(false);
        return;
      }
      const ok = await checkHaciendaDisponibles({
        empresaId: empresaSeleccionada.id,
        proveedorId: selectedComprobante.proveedor_id,
        comprobanteId: selectedComprobante.id, // << clave
      });
      setHasHaciendaDisponiblesEdit(ok);
    })();
  }, [showModal, selectedComprobante?.proveedor_id, selectedComprobante?.hacienda_id, empresaSeleccionada?.id, checkHaciendaDisponibles]);

  const CAMPOS_NUMERICOS_COMPROBANTE = [
    "neto",
    "iva105",
    "iva21",
    "iva_especial",
    "iva_especial_porcentaje",
    "percepcion_iva",
    "percepcion_ganancias",
    "percepcion_iibb",
    "impuestos_internos",
    "exento_no_gravado",
    "total",
    "montoreal",
    "diferenciaefectivo",
    "retencion",
  ];

  const handleFocusNumero = (e) => {
    const { name, value } = e.target;

    if (
      CAMPOS_NUMERICOS_COMPROBANTE.includes(name) &&
      Number(value) === 0
    ) {
      e.target.select();
    }
  };

  const handleCloseModal = () => {
    setSelectedComprobante(null);
    setPagosComprobante([]);
    setPagosProgramadosComprobante([]);
    setPagosProgramadosDesasociar([]);
    setSituacionCtaCteComprobante(null);
    setAjustesComprobante([]);
    setPuedeEditarComprobante(false);
    setMotivoNoEditar("");
    setShowModal(false);
  };

  const toggleDesasociarPagoProgramado = (pagoProgramado) => {

    const pagoProgramadoId =
      Number(pagoProgramado?.id);

    if (!pagoProgramadoId) {
      return;
    }


    const yaMarcado =
      pagosProgramadosDesasociar.some(
        (id) =>
          Number(id) ===
          pagoProgramadoId
      );


    /*
     * Si ya estaba marcado, simplemente
     * deshacemos la decisión.
     *
     * No hace falta mostrar confirmación.
     */
    if (yaMarcado) {

      setPagosProgramadosDesasociar(
        (prev) =>
          prev.filter(
            (id) =>
              Number(id) !==
              pagoProgramadoId
          )
      );

      return;
    }


    /*
     * IMPORTANTE:
     *
     * Desasociar un Pago Programado será
     * una operación GLOBAL.
     *
     * Todavía NO modificamos la BD.
     * La operación se ejecutará recién
     * cuando se presione "Guardar cambios".
     */
    const confirmar =
      window.confirm(
        "Este Pago Programado se desasociará de todos los comprobantes relacionados. El cambio se aplicará al guardar\n\n" +
        "¿Desea continuar?"
      );


    if (!confirmar) {
      return;
    }


    /*
     * Solamente lo marcamos.
     *
     * NO hacemos fetch.
     * NO modificamos BD.
     */
    setPagosProgramadosDesasociar(
      (prev) => [
        ...prev,
        pagoProgramadoId,
      ]
    );
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedComprobante((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name.endsWith("_id") ||
            CAMPOS_NUMERICOS_COMPROBANTE.includes(name)
            ? value === ""
              ? ""
              : value
            : value,
    }));
  };

  const handleGuardarCambios = async () => {

    if (guardandoEdicion) return;

    try {

      /*
       * Si el comprobante está bloqueado para edición,
       * pero el usuario marcó uno o más Pagos Programados
       * para desasociar, permitimos exclusivamente
       * confirmar esa operación.
       */
      const soloDesasociandoPagoProgramado =
        !puedeEditarComprobante &&
        pagosProgramadosDesasociar.length > 0;


      /*
       * Las validaciones normales del comprobante
       * solamente se ejecutan cuando realmente estamos
       * editando sus datos.
       *
       * Si solamente estamos desasociando un PP,
       * no validamos campos históricos del comprobante.
       */
      if (!soloDesasociandoPagoProgramado) {

        const faltanEdit = validarObligatorios(
          selectedComprobante,
          [
            "observaciones",
            "hacienda_id",

            "iva_especial",
            "iva_especial_porcentaje",
            "percepcion_iva",
            "percepcion_ganancias",
            "percepcion_iibb",
            "impuestos_internos",
            "exento_no_gravado",
          ]
        );

        if (faltanEdit.length) {
          alert(
            "Faltan completar campos obligatorios: " +
            faltanEdit.join(", ")
          );
          return;
        }
      }


      setGuardandoEdicion(true);
      const body =
        soloDesasociandoPagoProgramado
          ? {
            /*
             * Comprobante bloqueado:
             * enviamos EXCLUSIVAMENTE la operación
             * de desasociación.
             */
            pagos_programados_desasociar:
              pagosProgramadosDesasociar,
          }
          : {
            /*
             * Edición normal del comprobante.
             */
            ...selectedComprobante,

            hacienda_id:
              selectedComprobante?.hacienda_id
                ? Number(
                  selectedComprobante.hacienda_id
                )
                : null,

            pagos_programados_desasociar:
              pagosProgramadosDesasociar,
          };

      const res = await fetch(
        `${apiUrl}/comprobantes-egreso/${selectedComprobante.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(body),
        }
      );

      const data =
        await res
          .json()
          .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
          "No se pudo actualizar el comprobante"
        );
      }

      await loadComprobantes();

      handleCloseModal();

    } catch (error) {

      console.error(
        "❌ Error al actualizar comprobante:",
        error
      );

      alert(
        error.message ||
        "No se pudo actualizar el comprobante."
      );

    } finally {

      setGuardandoEdicion(false);

    }
  };

  const handleEliminar = async (comp) => {
    if (!puedeEliminarComprobante(comp)) {
      alert(
        "Sólo se pueden eliminar comprobantes impagos, notas de crédito sin pagos o comprobantes pagados íntegramente mediante compensación."
      );
      return;
    }
    if (!window.confirm(`¿Eliminar el comprobante #${comp.id}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setDeletingId(comp.id);
      const res = await fetch(`${apiUrl}/comprobantes-egreso/${comp.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "No se pudo eliminar el comprobante");

      await loadComprobantes();
    } catch (err) {
      console.error("❌ Error al eliminar comprobante:", err);
      alert(err.message || "No se pudo eliminar el comprobante");
    } finally {
      setDeletingId(null);
    }
  };


  // === Creación
  const handleNuevoChange = (e) => {

    const {
      name,
      value,
      type,
      checked,
    } = e.target;


    /*
     * FECHA COMPROBANTE
     *
     * Cuando cambia la fecha:
     *
     * 1. buscamos Libro IVA del mismo mes/año;
     * 2. si no existe, usamos el más reciente;
     * 3. el usuario después puede cambiar
     *    manualmente el Libro IVA.
     */
    if (name === "fechacomprobante") {

      const libroAutomatico =
        obtenerLibroIVAPorFecha(value);

      setNuevoComprobante((prev) => ({
        ...prev,

        fechacomprobante:
          value,

        libroiva_id:
          libroAutomatico?.id
            ? String(libroAutomatico.id)
            : "",
      }));

      return;
    }


    /*
     * RESTO DE LOS CAMPOS
     */
    setNuevoComprobante((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : name.endsWith("_id") ||
            CAMPOS_NUMERICOS_COMPROBANTE.includes(name)
            ? value === ""
              ? ""
              : value
            : value,
    }));

  };

  const getImputacionFromCategoria = (categoriaId) => {
    const cat = categoriasEgreso?.find((c) => Number(c.id) === Number(categoriaId));
    return cat?.imputacioncontable_id ?? null;
  };

  const agregarAjuste = () => {
    setAjustes((prev) => [
      ...prev,
      {
        tipo: "disminuye",
        concepto: "",
        importe: "",
        detalle: "",
        observaciones: "",
      },
    ]);
  };

  const actualizarAjuste = (idx, campo, valor) => {
    setAjustes((prev) =>
      prev.map((ajuste, i) =>
        i === idx
          ? {
            ...ajuste,
            [campo]: valor,
          }
          : ajuste
      )
    );
  };

  const eliminarAjuste = (idx) => {
    setAjustes((prev) =>
      prev.filter((_, i) => i !== idx)
    );
  };

  const handleCrearComprobante = async () => {

    if (creandoComprobante) return;

    try {
      // si hay transferencia nueva debe traer banco_id
      const transferenciaInvalida = pagos.some((p) => {
        const fp = formasPagoTesoreria.find((f) => Number(f.id) === Number(p.formapago_id));
        const esTransf = /transfer/i.test(fp?.descripcion || "");
        return esTransf && !p?.existing_ref && (!p?.banco_id || String(p.banco_id).trim() === "");
      });
      if (transferenciaInvalida) {
        alert("Falta Banco/Cuenta en una transferencia.");
        return;
      }

      if (!empresaSeleccionada) {
        alert("Debes seleccionar una empresa.");
        return;
      }

      // Guard temprano para cta cte incompleta (fecha_pago o forma futura)
      const hayCtacteIncompleta = pagos.some((p) => {
        const fp = formasPagoTesoreria.find((f) => Number(f.id) === Number(p.formapago_id));
        const esCC = /cta.?cte|cuenta corriente/i.test((fp?.descripcion || "").toLowerCase());
        return esCC && !p?.existing_ref && (!p?.fecha_pago || !p?.formapago_futuro_id);
      });
      if (hayCtacteIncompleta) {
        alert("Hay pagos en Cuenta Corriente sin 'Fecha de pago' o 'Forma de pago acordada'.");
        return;
      }

      // Los pagos deben cubrir el total financiero luego de aplicar ajustes
      const totalReferencia =
        totalParaPagos;

      const sumaPagosLocal =
        pagos.reduce(
          (acc, it) =>
            acc + (Number(it.monto) || 0),
          0
        );
      if (Math.abs(totalReferencia - sumaPagosLocal) > 0.009) {
        alert(
          `La suma de las formas de pago debe coincidir con el total financiero del comprobante (${fmtMoney(totalReferencia)}).`
        );
        return;
      }

      const byId = (fpId) => formasPagoTesoreria.find((f) => Number(f.id) === Number(fpId));
      const has = (fpId, regex) => {
        const fp = byId(fpId);
        return fp ? regex.test(fp.descripcion || "") : false;
      };
      const esPagoEnCajaDesdeId = (fpId) => has(fpId, /caja|efectivo/i);
      const medioFromFp = (fpId) => {
        if (has(fpId, /compensaci[oó]n/i)) return "compensacion";
        if (has(fpId, /caja|efectivo/i)) return "caja";
        if (has(fpId, /transfer/i)) return "transferencia";
        if (has(fpId, /e-?cheq|echeq/i)) return "echeq";
        if (has(fpId, /tarjeta/i)) return "tarjeta";
        if (has(fpId, /cta.?cte|cuenta corriente/i)) return "ctacte";

        return "desconocido";
      };

      const header_formapago_id =
        pagos.length === 1 ? (pagos[0]?.formapago_id ? Number(pagos[0].formapago_id) : null) : null;



      let header_imputacion_id = null;
      for (const p of pagos) {
        const imp = p.imputacioncontable_id
          ? Number(p.imputacioncontable_id)
          : p.categoriaegreso_id
            ? Number(getImputacionFromCategoria(p.categoriaegreso_id))
            : null;
        if (imp) {
          header_imputacion_id = imp;
          break;
        }
      }
      if (!header_imputacion_id && nuevoComprobante.categoriaegreso_id) {
        const impHeader = getImputacionFromCategoria(nuevoComprobante.categoriaegreso_id);
        if (impHeader) header_imputacion_id = Number(impHeader);
      }

      if (pagos.length === 1 && !header_formapago_id) {
        alert("Falta seleccionar la forma de pago.");
        return;
      }
      if (!header_imputacion_id) {
        alert("Falta imputación contable (se toma de la categoría del pago o del encabezado).");
        return;
      }
      const faltaImputacionEnTodo =
        !header_imputacion_id &&
        !pagos.some(
          (p) =>
            p.imputacioncontable_id ||
            (p.categoriaegreso_id && getImputacionFromCategoria(p.categoriaegreso_id))
        );
      if (faltaImputacionEnTodo) {
        alert(
          "Debes seleccionar una categoría (en el encabezado o en alguna forma de pago) para derivar la imputación contable."
        );
        return;
      }

      // Armamos el encabezado SIN formapago_id de entrada
      const comprobanteBase = {
        nrocomprobante:
          nuevoComprobante.nrocomprobante,

        neto:
          Number(nuevoComprobante.neto || 0),

        iva105:
          Number(nuevoComprobante.iva105 || 0),

        iva21:
          Number(nuevoComprobante.iva21 || 0),

        iva_especial:
          Number(
            nuevoComprobante.iva_especial || 0
          ),

        iva_especial_porcentaje:
          nuevoComprobante.iva_especial_porcentaje === "" ||
            nuevoComprobante.iva_especial_porcentaje == null
            ? null
            : Number(
              nuevoComprobante.iva_especial_porcentaje
            ),

        percepcion_iva:
          Number(
            nuevoComprobante.percepcion_iva || 0
          ),

        percepcion_ganancias:
          Number(
            nuevoComprobante.percepcion_ganancias || 0
          ),

        percepcion_iibb:
          Number(
            nuevoComprobante.percepcion_iibb || 0
          ),

        impuestos_internos:
          Number(
            nuevoComprobante.impuestos_internos || 0
          ),

        exento_no_gravado:
          Number(
            nuevoComprobante.exento_no_gravado || 0
          ),

        total:
          Number(nuevoComprobante.total || 0),

        tipocomprobante_id:
          nuevoComprobante.tipocomprobante_id
            ? Number(nuevoComprobante.tipocomprobante_id)
            : null,

        // ... acá continúa exactamente
        // el resto que ya tenés
        ptoventa_id: nuevoComprobante.ptoventa_id ? Number(nuevoComprobante.ptoventa_id) : null,
        libroiva_id: nuevoComprobante.libroiva_id ? Number(nuevoComprobante.libroiva_id) : null,
        proveedor_id: nuevoComprobante.proveedor_id ? Number(nuevoComprobante.proveedor_id) : null,
        fechacomprobante: nuevoComprobante.fechacomprobante,
        fechavencimiento: nuevoComprobante.fechavencimiento || null,
        imputacioncontable_id: header_imputacion_id,
        observaciones: nuevoComprobante.observaciones || null,
        // ctacteproveedor_id: nuevoComprobante.ctacteproveedor_id
        //   ? Number(nuevoComprobante.ctacteproveedor_id)
        //   : null,
        conFactura: !!nuevoComprobante.conFactura,
        fechapago: nuevoComprobante.fechapago,
        montoreal: Number(nuevoComprobante.montoreal || 0),
        diferenciaefectivo: Number(nuevoComprobante.diferenciaefectivo || 0),
        hacienda_id: nuevoComprobante.hacienda_id
          ? Number(nuevoComprobante.hacienda_id)
          : null,
        letra: nuevoComprobante.letra || "",
        retencion: Number(nuevoComprobante.retencion || 0),
        empresa_id: empresaSeleccionada.id,
      };

      // ⚠️ Sólo si hay UNA forma de pago seteamos formapago_id en el encabezado
      if (header_formapago_id != null) {
        comprobanteBase.formapago_id = header_formapago_id;
      }


      const numOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

      // normalización (incluye existing_ref y gastoestimado en cada fila)
      const pagosNormalizados = pagos.map((p) => {
        const medio = medioFromFp(p.formapago_id);
        const usaExistente = !!p?.existing_ref;

        const imputacionPago =
          p.imputacioncontable_id
            ? Number(p.imputacioncontable_id)
            : p.categoriaegreso_id
              ? Number(getImputacionFromCategoria(p.categoriaegreso_id))
              : nuevoComprobante.categoriaegreso_id
                ? Number(getImputacionFromCategoria(nuevoComprobante.categoriaegreso_id))
                : null;

        const numOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

        const base = {
          medio,
          // OJO: default usa formapago_id de la fila…
          formapago_id: p.formapago_id ? Number(p.formapago_id) : null,
          monto: Number(p.monto || 0),
          fecha: usaExistente
            ? (p.fecha || null)
            : (nuevoComprobante.fechapago || null),
          detalle: p.detalle || null,
          categoriaegreso_id: p.categoriaegreso_id
            ? Number(p.categoriaegreso_id)
            : nuevoComprobante.categoriaegreso_id
              ? Number(nuevoComprobante.categoriaegreso_id)
              : null,
          imputacioncontable_id: imputacionPago,
          existing_ref: usaExistente ? { ...p.existing_ref } : null,
          caja_id:
            !usaExistente && esPagoEnCajaDesdeId(p.formapago_id)
              ? (p.caja_id ? Number(p.caja_id) : (cajaAbierta?.caja?.id ?? null))
              : null,
          gastoestimado:
            !usaExistente && p?.gastoestimado?.aplicar && p?.gastoestimado?.instancia_id
              ? {
                instancia_id: Number(p.gastoestimado.instancia_id),
                cancelar_renovacion: Boolean(p.gastoestimado.cancelar_renovacion),
              }
              : null,
        };

        if (!usaExistente) {
          if (medio === "tarjeta") {
            base.tipotarjeta_id = numOrNull(p.tipotarjeta_id);
            base.marcatarjeta_id = numOrNull(p.marcatarjeta_id);
            base.cupon_numero = (p.cupon_numero ?? "").toString().trim() || null;
            base.planpago_id = numOrNull(p.planpago_id);
          }

          if (medio === "echeq") {
            base.banco_id = numOrNull(p.banco_id);
            base.numero_echeq = (p.numero_echeq ?? "").toString().trim() || null;
            base.fecha_vencimiento = p.fecha_vencimiento || null;
          }

          if (medio === "transferencia") {
            base.banco_id = numOrNull(p.banco_id);
            base.referencia = (p.referencia ?? "").toString().trim() || null;
            base.cbu_alias_destino = (p.cbu_alias_destino ?? "").toString().trim() || null;
            base.titular_destino = (p.titular_destino ?? "").toString().trim() || null;
          }

          if (medio === "ctacte") {
            // ⬇️ EL PUNTO CRÍTICO: usar la forma futura como formapago_id
            base.formapago_id = p.formapago_futuro_id ? Number(p.formapago_futuro_id) : null;
            base.fecha_pago = p.fecha_pago || null;
          }
        }

        return base;
      });

      const ajustesNormalizados = ajustes.map((a) => ({
        medio: "ajuste",
        tipo_ajuste: a.tipo,
        monto: Number(a.importe || 0),

        fecha:
          nuevoComprobante.fechacomprobante ||
          nuevoComprobante.fechapago ||
          null,

        concepto: String(a.concepto || "").trim(),

        detalle:
          String(a.detalle || "").trim() ||
          null,

        observaciones:
          String(a.observaciones || "").trim() ||
          null,

        referencia_tipo:
          a.referencia_tipo ||
          null,

        referencia_id:
          a.referencia_id ||
          null,
      }));

      const ajusteInvalido =
        ajustesNormalizados.find(
          (a) =>
            !["aumenta", "disminuye"].includes(
              String(a.tipo_ajuste || "")
                .trim()
                .toLowerCase()
            ) ||
            !String(a.concepto || "").trim() ||
            !(Number(a.monto || 0) > 0)
        );

      if (ajusteInvalido) {
        alert(
          "Todos los ajustes deben tener tipo, concepto e importe mayor a cero."
        );
        return;
      }
      if (totalParaPagos < 0) {
        alert(
          "Los ajustes no pueden dejar un total financiero negativo."
        );
        return;
      }
      const payload = {
        empresa_id: empresaSeleccionada.id,
        comprobante: comprobanteBase,

        pagos: [
          ...pagosNormalizados,
          ...ajustesNormalizados,
        ],
      };

      console.log("emitir comprobante payload:", JSON.parse(JSON.stringify(payload)));

      const EXC_OPTIONALES = [
        "observaciones",
        "hacienda_id",
        "iva_especial_porcentaje",
      ];

      const faltanHeader = validarObligatorios(payload.comprobante, EXC_OPTIONALES);
      if (faltanHeader.length) {
        alert("Faltan completar campos obligatorios: " + faltanHeader.join(", "));
        return;
      }

      if (!pagosNormalizados.length) {
        alert("Debes cargar al menos una forma de pago.");
        return;
      }

      const erroresPagos = validarPagos(pagosNormalizados);

      if (erroresPagos.length) {
        alert(
          "Revisá las formas de pago:\n" +
          erroresPagos.join("\n")
        );
        return;
      }


      // A partir de acá ya pasaron todas las validaciones.
      // Bloqueamos el botón antes de enviar al backend.
      setCreandoComprobante(true);

      console.log(
        "🔵 PAYLOAD FINAL COMPROBANTE:",
        JSON.stringify(payload, null, 2)
      );

      const res = await fetch(
        `${apiUrl}/comprobantes-egreso/emitir`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "No se pudo emitir el comprobante");
      }

      await loadComprobantes();
      setShowCreateModal(false);
      resetCreateForm();
    } catch (error) {

      console.error(
        "❌ Error al emitir comprobante:",
        error
      );

      alert(
        error.message ||
        "No se pudo emitir el comprobante."
      );

    } finally {

      setCreandoComprobante(false);

    }
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const resetProveedorModal = () => {
    setNuevoProveedorModal({
      nombre: "",
      direccion: "",
      telefono: "",
      email: "",
      cuit: "",
      dni: "",
      imputacioncontable_id: "",
      formapago_id: "",
    });
    setErrorCrearProveedor("");
  };

  const handleOpenProveedorCreateModal = () => {
    resetProveedorModal();
    setShowProveedorCreateModal(true);
  };

  const handleCloseProveedorCreateModal = () => {
    resetProveedorModal();
    setShowProveedorCreateModal(false);
  };

  const handleNuevoProveedorModalChange = (e) => {
    const { name, value } = e.target;

    setNuevoProveedorModal((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errorCrearProveedor) {
      setErrorCrearProveedor("");
    }
  };

  const handleCrearProveedorDesdeComprobante = async () => {
    if (!nuevoProveedorModal.cuit || nuevoProveedorModal.cuit.trim() === "") {
      setErrorCrearProveedor("El CUIT es obligatorio");
      return;
    }

    try {
      setLoadingCrearProveedor(true);
      setErrorCrearProveedor("");

      const response = await fetch(`${apiUrl}/proveedores/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...nuevoProveedorModal,
          cuit: nuevoProveedorModal.cuit.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setErrorCrearProveedor(data.error || "Error al crear el proveedor");
        return;
      }

      const proveedorCreado = data;

      setProveedoresOptions((prev) =>
        [...prev, proveedorCreado].sort((a, b) =>
          (a.nombre || "").localeCompare(b.nombre || "")
        )
      );

      setNuevoComprobante((prev) => ({
        ...prev,
        proveedor_id: proveedorCreado.id,
      }));

      if (empresaSeleccionada?.id && proveedorCreado?.id) {
        const ok = await checkHaciendaDisponibles({
          empresaId: empresaSeleccionada.id,
          proveedorId: proveedorCreado.id,
        });
        setHasHaciendaDisponiblesCreate(ok);
      } else {
        setHasHaciendaDisponiblesCreate(false);
      }

      handleCloseProveedorCreateModal();
    } catch (error) {
      console.error("Error al crear proveedor desde comprobante:", error);
      setErrorCrearProveedor("Error al crear el proveedor");
    } finally {
      setLoadingCrearProveedor(false);
    }
  };

  // ====== Totales, ajustes y pagos (creación)

  const totalBaseFinanciero = useMemo(
    () =>
      esLCDCreate
        ? Number(nuevoComprobante.montoreal || 0)
        : Number(nuevoComprobante.total || 0),
    [
      esLCDCreate,
      nuevoComprobante.montoreal,
      nuevoComprobante.total,
    ]
  );

  const totalAjustesAumenta = useMemo(
    () =>
      ajustes
        .filter((a) => a.tipo === "aumenta")
        .reduce(
          (acc, a) =>
            acc + Number(a.importe || 0),
          0
        ),
    [ajustes]
  );

  const totalAjustesDisminuye = useMemo(
    () =>
      ajustes
        .filter((a) => a.tipo === "disminuye")
        .reduce(
          (acc, a) =>
            acc + Number(a.importe || 0),
          0
        ),
    [ajustes]
  );

  const totalAjustes =
    totalAjustesAumenta -
    totalAjustesDisminuye;

  const totalParaPagos = round2(
    totalBaseFinanciero +
    totalAjustes
  );

  const sumaPagos = pagos.reduce(
    (acc, it) =>
      acc + (Number(it.monto) || 0),
    0
  );

  const montosOk =
    totalParaPagos >= 0 &&
    Math.abs(
      totalParaPagos - sumaPagos
    ) < 0.009;

  const imputHeaderDesc = useMemo(() => {
    const it = imputacionContableTabla.find(
      (x) => Number(x.id) === Number(nuevoComprobante.imputacioncontable_id)
    );
    return (
      it?.descripcion ||
      (nuevoComprobante.imputacioncontable_id
        ? `ID ${nuevoComprobante.imputacioncontable_id}`
        : "")
    );
  }, [imputacionContableTabla, nuevoComprobante.imputacioncontable_id]);

  const getFormaPagoDesc = (fpId) => {
    if (!fpId) return null;

    const fp = formasPagoTesoreria.find(
      (f) =>
        Number(f.id) ===
        Number(fpId)
    );

    return fp?.descripcion || `FP #${fpId}`;
  };


  /*
   * Pagos que deben mostrarse en el detalle.
   *
   * Si la Cta.Cte. ya quedó totalmente cubierta
   * mediante un Pago Programado, dejamos de mostrar
   * la Cta.Cte. como forma pendiente.
   */
  const pagosComprobanteVisibles =
    pagosComprobante.filter((p) => {

      if (
        !situacionCtaCteComprobante
          ?.totalmente_cubierta
      ) {
        return true;
      }

      if (
        pagosProgramadosComprobante.length === 0
      ) {
        return true;
      }

      const tipo =
        String(
          p.tipo ||
          p.medio ||
          p.formapago ||
          p.descripcion ||
          ""
        )
          .trim()
          .toLowerCase();

      const esCtaCte =
        tipo.includes("cta cte") ||
        tipo.includes("cta. cte") ||
        tipo.includes("cuenta corriente") ||
        tipo === "ctacte";

      return !esCtaCte;
    });


  return (
    <Container>
      <h1 className="my-list-title dark-text">Comprobantes de Egreso</h1>

      {/* === Barra de filtros === */}
      <div className="p-3 mb-3 border rounded bg-light">
        <Row className="g-2">
          <Col md={3}>
            <Form.Label>Proveedor</Form.Label>
            <Form.Select
              name="proveedor_id"
              value={filtros.proveedor_id}
              onChange={onFiltroChange}
              className="form-control my-input"
            >
              <option value="">Todos</option>
              {proveedoresTabla.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Label>Estado</Form.Label>
            <Form.Select name="estado" value={filtros.estado} onChange={onFiltroChange} className="form-control my-input">
              <option value="">Todos</option>
              <option value="pagada">Pagada</option>
              <option value="impaga">Impaga</option>
              <option value="parcial">Parcial</option>
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Forma de pago</Form.Label>
            <Form.Select
              name="formapago_id"
              value={filtros.formapago_id}
              onChange={onFiltroChange}
              className="form-control my-input"
            >
              <option value="">Todas</option>
              <option value="varios">VARIOS (mixto)</option>
              {formasPagoTesoreria.map((fp) => (
                <option key={fp.id} value={fp.id}>
                  {fp.descripcion}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Label>Desde</Form.Label>
            <Form.Control
              type="date"
              name="fecha_desde"
              value={filtros.fecha_desde}
              onChange={onFiltroChange}
            />
          </Col>

          <Col md={2}>
            <Form.Label>Hasta</Form.Label>
            <Form.Control
              type="date"
              name="fecha_hasta"
              value={filtros.fecha_hasta}
              onChange={onFiltroChange}
            />
          </Col>

          <Col md={12}>
            <Form.Label>Búsqueda</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="N° comprobante, letra u observaciones…"
                name="texto"
                value={filtros.texto}
                onChange={onFiltroChange}
              />
              <Button variant="outline-secondary" onClick={limpiarFiltros} className="mx-2">
                Limpiar
              </Button>
            </InputGroup>
          </Col>
        </Row>
      </div>

      <div className="mb-2 d-flex justify-content-between align-items-center">
        <div className="text-muted">
          {comprobantesFiltrados.length} resultado(s) · Total filtrado:{" "}
          <strong>{fmtMoney(totalFiltrado)}</strong>
        </div>

        <div>
          <Button
            variant="outline-success"
            className="mx-2"
            onClick={exportarExcel}
            disabled={comprobantesOrdenados.length === 0}
          >
            Exportar Excel
          </Button>

          <Button
            className="mx-2"
            variant="success"
            disabled={!empresaSeleccionada}
            onClick={() => {

              if (!empresaSeleccionada) {

                alert(
                  "Debe seleccionar una empresa para emitir comprobantes."
                );

                return;
              }


              const inicial =
                makeInitialNuevo();

              const libroAutomatico =
                obtenerLibroIVAPorFecha(
                  inicial.fechacomprobante
                );


              setPagos([]);
              setAjustes([]);

              setNuevoComprobante({
                ...inicial,

                libroiva_id:
                  libroAutomatico?.id
                    ? String(libroAutomatico.id)
                    : "",
              });

              setShowCreateModal(true);

            }}
          >
            Ingresar Comprobante
          </Button>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <Table striped bordered hover size="sm" className="align-middle">
          <thead>
            <tr>
              {[
                ["id", "#"],
                ["nrocomprobante", "Comprobante"],
                ["tipo", "Tipo"],
                ["fechacomprobante", "Fecha"],
                ["neto", "Neto"],
                ["iva105", "IVA 10,5 %"],
                ["iva21", "IVA 21 %"],
                ["iva_especial", "IVA especial"],
                ["total", "Total"],
                ["proveedor", "Proveedor"],
                ["empresa", "Empresa"],
                ["hacienda_id", "Hacienda"],
                ["formapago", "Forma de Pago"],
                ["estadopago", "Estado Pago"],
              ].map(([campo, titulo]) => (
                <th
                  key={campo}
                  onClick={() => cambiarOrden(campo)}
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  {titulo}
                  {orden.campo === campo
                    ? orden.direccion === "asc" ? " ▲" : " ▼"
                    : ""}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {comprobantesPaginados.map((comp) => (
              <tr
                key={comp.id}
                onDoubleClick={() => handleDoubleClick(comp)}
                style={{ cursor: "pointer" }}
              >
                <td>{comp.id}</td>
                <td>{comp.nrocomprobante}</td>
                <td>
                  {tiposComprobanteById[comp.tipocomprobante_id] ||
                    comp.tipocomprobante_id}
                </td>
                <td>{comp.fechacomprobante || ""}</td>
                <td className="text-end">{fmtMoney(comp.neto)}</td>
                <td className="text-end">{fmtMoney(comp.iva105)}</td>
                <td className="text-end">{fmtMoney(comp.iva21)}</td>
                <td className="text-end">{fmtMoney(comp.iva_especial ?? 0)}</td>
                <td className="text-end">{fmtMoney(comp.total)}</td>
                <td>
                  {proveedoresTabla.find(
                    (p) => Number(p.id) === Number(comp.proveedor_id)
                  )?.nombre || comp.proveedor_id}
                </td>
                <td>
                  {empresasById[comp.empresa_id] || comp.empresa_id}
                </td>
                <td>{comp.hacienda_id || "-"}</td>
                <td>
                  {comp.formapago_id
                    ? formasPagoTesoreria.find(
                      (fp) => Number(fp.id) === Number(comp.formapago_id)
                    )?.descripcion || comp.formapago_id
                    : "VARIOS"}
                </td>
                <td>{comp.estadopago}</td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={
                      !puedeEliminarComprobante(comp) ||
                      deletingId === comp.id
                    }
                    onClick={() => handleEliminar(comp)}
                    title={
                      puedeEliminarComprobante(comp)
                        ? "Eliminar"
                        : "Sólo comprobantes IMPAGOS o notas de crédito sin pago"
                    }
                  >
                    {deletingId === comp.id ? "Eliminando…" : "Eliminar"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <span>Mostrar</span>

          <Form.Select
            size="sm"
            style={{ width: 90 }}
            value={registrosPorPagina}
            onChange={(e) => setRegistrosPorPagina(Number(e.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>

          <span>
            de {comprobantesOrdenados.length} registros
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={paginaVisible === 1}
            onClick={() => setPaginaActual(1)}
          >
            Primera
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            disabled={paginaVisible === 1}
            onClick={() => setPaginaActual(paginaVisible - 1)}
          >
            Anterior
          </Button>

          <span>
            Página {paginaVisible} de {totalPaginas}
          </span>

          <Button
            variant="outline-secondary"
            size="sm"
            disabled={paginaVisible === totalPaginas}
            onClick={() => setPaginaActual(paginaVisible + 1)}
          >
            Siguiente
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            disabled={paginaVisible === totalPaginas}
            onClick={() => setPaginaActual(totalPaginas)}
          >
            Última
          </Button>
        </div>
      </div>


      {/* ===== Modal de edición ===== */}
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {puedeEditarComprobante
              ? "Editar Comprobante"
              : "Detalle del Comprobante"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedComprobante && (
            <>
              {!puedeEditarComprobante && (
                <div className="alert alert-warning mb-3">
                  <strong>Comprobante sólo para consulta.</strong>

                  <div>
                    {motivoNoEditar ||
                      "Este comprobante no puede ser editado."}
                  </div>
                </div>
              )}

              <Form>
                <fieldset disabled={!puedeEditarComprobante}>
                  <div className="row">
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Letra</Form.Label>
                      <Form.Control
                        name="letra"
                        value={selectedComprobante.letra || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>N° Comprobante</Form.Label>
                      <Form.Control
                        name="nrocomprobante"
                        value={selectedComprobante.nrocomprobante || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Proveedor</Form.Label>
                      <Form.Select
                        name="proveedor_id"
                        value={selectedComprobante.proveedor_id || ""}
                        className="form-control my-input"
                        disabled
                      >
                        <option value="">Seleccione...</option>
                        {proveedoresTabla.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>Categoría de Egreso (encabezado)</Form.Label>
                      <Form.Select
                        name="categoriaegreso_id"
                        value={selectedComprobante.categoriaegreso_id || ""}
                        onChange={(e) => {

                          handleChange(e);

                          const imputacionId =
                            getImputacionFromCategoria(
                              e.target.value
                            );

                          setSelectedComprobante(
                            (prev) => ({
                              ...prev,

                              categoriaegreso_id:
                                e.target.value,

                              imputacioncontable_id:
                                imputacionId || "",
                            })
                          );

                        }}
                        className="form-control my-input"
                      >
                        <option value="">Seleccione...</option>
                        {categoriasEgreso.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nombre}
                          </option>
                        ))}
                      </Form.Select>
                      <small className="text-muted">
                        Si no elegís categoría en un pago, se usará esta para derivar la imputación
                        contable.
                      </small>
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>Imputación Contable (encabezado)</Form.Label>
                      <Form.Select
                        name="imputacioncontable_id"
                        value={selectedComprobante.imputacioncontable_id || ""}
                        onChange={handleChange}
                        className="form-control my-input"
                      >
                        <option value="">Seleccione...</option>
                        {imputacionContableTabla.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>Empresa</Form.Label>
                      <Form.Control
                        value={
                          empresaSeleccionada?.nombre ||
                          empresasById[selectedComprobante?.empresa_id] ||
                          selectedComprobante?.empresa_id ||
                          ""
                        }
                        readOnly
                        plaintext
                      />
                    </Form.Group>
                  </div>

                  <div className="row">

                    {/* NETO */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Neto</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="neto"
                        value={selectedComprobante.neto ?? 0}
                        onChange={handleChange}
                        onFocus={handleFocusNumero}
                      />
                    </Form.Group>


                    {/* IVA 10,5% */}
                    <Form.Group className="mb-3 col-md-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label className="mb-0">
                          IVA 10,5%
                        </Form.Label>

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={calcIVA105Edit}
                        >
                          calc
                        </Button>
                      </div>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="iva105"
                        value={selectedComprobante.iva105 ?? 0}
                        onChange={handleChange}
                      />
                    </Form.Group>


                    {/* IVA 21% */}
                    <Form.Group className="mb-3 col-md-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label className="mb-0">
                          IVA 21%
                        </Form.Label>

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={calcIVA21Edit}
                        >
                          calc
                        </Button>
                      </div>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="iva21"
                        value={selectedComprobante.iva21 ?? 0}
                        onChange={handleChange}
                      />
                    </Form.Group>


                    {/* IVA ESPECIAL % */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>IVA especial %</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="iva_especial_porcentaje"
                        value={selectedComprobante.iva_especial_porcentaje ?? ""}
                        onChange={handleChange}
                      />
                    </Form.Group>


                    {/* IVA ESPECIAL IMPORTE */}
                    <Form.Group className="mb-3 col-md-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label className="mb-0">
                          IVA especial
                        </Form.Label>

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={calcIVAEspecialEdit}
                        >
                          calc
                        </Button>
                      </div>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="iva_especial"
                        value={selectedComprobante.iva_especial ?? 0}
                        onChange={handleChange}
                      />
                    </Form.Group>


                    {/* PERCEPCIÓN IVA */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Percep. IVA</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="percepcion_iva"
                        value={selectedComprobante.percepcion_iva ?? 0}
                        onChange={handleNuevoChange}
                        onFocus={handleFocusNumero}
                      />
                    </Form.Group>


                    {/* PERCEPCIÓN GANANCIAS */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Percep. Ganancias</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="percepcion_ganancias"
                        value={selectedComprobante.percepcion_ganancias ?? 0}
                        onChange={handleChange}
                      />
                    </Form.Group>


                    {/* PERCEPCIÓN IIBB */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Percep. IIBB</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="percepcion_iibb"
                        value={selectedComprobante.percepcion_iibb ?? 0}
                        onChange={handleNuevoChange}
                        onFocus={handleFocusNumero}
                      />
                    </Form.Group>


                    {/* IMPUESTOS INTERNOS */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Imp. Internos</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="impuestos_internos"
                        value={selectedComprobante.impuestos_internos ?? 0}
                        onChange={handleNuevoChange}
                        onFocus={handleFocusNumero}
                      />
                    </Form.Group>


                    {/* EXENTO / NO GRAVADO */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Exento / No Grav.</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="exento_no_gravado"
                        value={selectedComprobante.exento_no_gravado ?? 0}
                        onChange={handleNuevoChange}
                        onFocus={handleFocusNumero}
                      />
                    </Form.Group>
                    {/* RETENCIÓN */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Retención</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        name="retencion"
                        value={selectedComprobante?.retencion ?? 0}
                        onChange={handleChange}
                        onFocus={handleFocusNumero}
                      />
                    </Form.Group>


                    {/* TOTAL */}
                    <Form.Group className="mb-3 col-md-3">
                      <Form.Label>Total (auto)</Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="total"
                        value={selectedComprobante.total ?? 0}
                        readOnly
                      />
                    </Form.Group>

                  </div>

                  <div className="row">
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Tipo Comprobante</Form.Label>
                      <Form.Select
                        name="tipocomprobante_id"
                        value={selectedComprobante.tipocomprobante_id || ""}
                        onChange={handleChange}
                        className="form-control my-input"
                      >
                        <option value="">Seleccione...</option>
                        {tiposComprobanteTabla.map((tc) => (
                          <option key={tc.id} value={tc.id}>
                            {tc.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Punto de Venta</Form.Label>
                      <Form.Select
                        name="ptoventa_id"
                        value={selectedComprobante.ptoventa_id || ""}
                        onChange={handleChange}
                        className="form-control my-input"
                      >
                        <option value="">Seleccione...</option>
                        {ptosVtaFiltrados.map((pv) => (
                          <option key={pv.id} value={pv.id}>
                            {pv.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Libro IVA</Form.Label>
                      <Form.Select
                        name="libroiva_id"
                        value={selectedComprobante.libroiva_id || ""}
                        onChange={handleChange}
                        className="form-control my-input"
                      >
                        <option value="">Seleccione...</option>
                        {librosIVAFiltrados.map((l) => (
                          <option key={l.id} value={l.id}>
                            {`${l.mes} / ${l.anio}`}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Fecha Comprobante</Form.Label>
                      <Form.Control
                        type="date"
                        name="fechacomprobante"
                        value={selectedComprobante.fechacomprobante || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Fecha Vencimiento</Form.Label>
                      <Form.Control
                        type="date"
                        name="fechavencimiento"
                        value={selectedComprobante.fechavencimiento || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Fecha Pago</Form.Label>
                      <Form.Control
                        type="date"
                        name="fechapago"
                        value={selectedComprobante.fechapago || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Estado Pago</Form.Label>
                      <Form.Select
                        name="estadopago"
                        value={selectedComprobante.estadopago || "impaga"}
                        onChange={handleChange}
                        className="form-control my-input"
                      >
                        <option value="pagada">Pagada</option>
                        <option value="impaga">Impaga</option>
                        <option value="parcial">Parcial</option>
                      </Form.Select>
                    </Form.Group>

                    {/* Monto Real (solo editable en LCD) */}
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Monto Real {esLCDEdit ? "" : "(solo LCD)"}</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="montoreal"
                        value={esLCDEdit ? (selectedComprobante.montoreal || 0) : 0}
                        onChange={esLCDEdit ? handleChange : undefined}
                        onFocus={esLCDEdit ? handleFocusNumero : undefined}
                        readOnly={!esLCDEdit}
                      />
                    </Form.Group>

                    {/* Diferencia Efectivo (auto) */}
                    <Form.Group className="mb-3 col-md-4">
                      <Form.Label>Diferencia Efectivo (auto)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="diferenciaefectivo"
                        value={esLCDEdit ? (selectedComprobante.diferenciaefectivo || 0) : 0}
                        readOnly
                      />
                      <small className="text-muted">
                        {esLCDEdit ? "Se calcula: Monto real − Total." : "Disponible solo para LCD."}
                      </small>
                    </Form.Group>
                  </div>

                  <div className="row">
                    {/* <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Cuenta Cte. Proveedor (opcional)</Form.Label>
                  <Form.Control
                    name="ctacteproveedor_id"
                    value={selectedComprobante.ctacteproveedor_id || ""}
                    onChange={handleChange}
                  />
                </Form.Group> */}



                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>Hacienda (opcional)</Form.Label>
                      <InputGroup>
                        <Form.Control
                          name="hacienda_id"
                          value={selectedComprobante?.hacienda_id || ""}
                          onChange={handleChange}
                          placeholder="ID de Hacienda"
                        />
                        <Button
                          variant="outline-primary"
                          disabled={
                            !empresaSeleccionada?.id ||
                            !selectedComprobante?.proveedor_id
                          }
                          title={
                            !empresaSeleccionada?.id
                              ? "Seleccioná empresa"
                              : !selectedComprobante?.proveedor_id
                                ? "Seleccioná proveedor"
                                : hasHaciendaDisponiblesEdit
                                  ? "Buscar Hacienda disponibles"
                                  : "Sin Hacienda disponibles"
                          }
                          onClick={() => setShowHaciendaPickerEdit(true)}
                          className="mx-2"
                        >
                          Buscar
                        </Button>

                        {/* NUEVO: botón para quitar la Hacienda */}
                        <Button
                          variant="outline-danger"
                          disabled={!selectedComprobante?.hacienda_id}
                          title="Quitar Hacienda seleccionada"
                          onClick={() =>
                            setSelectedComprobante((prev) => ({ ...prev, hacienda_id: null }))
                          }
                          className="mx-2"
                        >
                          Quitar
                        </Button>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Doble click en una fila del modal para seleccionar. “Quitar” deja el campo vacío.
                      </Form.Text>
                    </Form.Group>

                  </div>

                  <div className="row">
                    <Form.Group className="mb-3 col-md-6 d-flex align-items-end">
                      <Form.Check
                        label="Con Factura"
                        name="conFactura"
                        checked={!!selectedComprobante.conFactura}
                        onChange={(e) =>
                          setSelectedComprobante((prev) => ({
                            ...prev,
                            conFactura: e.target.checked,
                          }))
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3 col-md-6">
                      <Form.Label>Imputación Contable</Form.Label>
                      <Form.Select
                        name="imputacioncontable_id"
                        value={selectedComprobante.imputacioncontable_id || ""}
                        onChange={handleChange}
                        className="form-control my-input"
                      >
                        <option value="">Seleccione...</option>
                        {imputacionContableTabla.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Observaciones</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="observaciones"
                      rows={2}
                      value={selectedComprobante.observaciones || ""}
                      onChange={handleChange}
                    />
                  </Form.Group>



                  <hr />

                  <h5 className="mb-3">
                    Ajustes aplicados
                  </h5>

                  {ajustesComprobante.length === 0 ? (

                    <div className="text-muted mb-3">
                      Este comprobante no tiene ajustes.
                    </div>

                  ) : (

                    <Table striped bordered hover size="sm">

                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Tipo</th>
                          <th>Concepto</th>
                          <th>Detalle</th>
                          <th>Importe</th>
                        </tr>
                      </thead>

                      <tbody>

                        {ajustesComprobante.map((a) => (

                          <tr key={`ajuste-${a.id}`}>

                            <td>
                              {a.fecha || "-"}
                            </td>

                            <td>
                              {a.tipo === "aumenta"
                                ? "Aumenta"
                                : "Disminuye"}
                            </td>

                            <td>
                              {a.concepto || "-"}
                            </td>

                            <td>
                              {a.detalle || "-"}
                            </td>

                            <td>
                              {a.tipo === "aumenta" ? "+" : "-"}{" "}
                              {fmtMoney(a.importe)}
                            </td>

                          </tr>

                        ))}

                      </tbody>

                    </Table>

                  )}

                </fieldset>

                <hr />

                <h5 className="mb-3">
                  Pagos programados / compromisos
                </h5>

                {pagosProgramadosComprobante.length === 0 ? (

                  <div className="text-muted mb-3">
                    Este comprobante no tiene pagos programados asociados.
                  </div>

                ) : (

                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    size="sm"
                    className="mb-3"
                  >

                    <thead>
                      <tr>
                        <th>Fecha programada</th>
                        <th>Pago programado</th>
                        <th>FP acordada</th>
                        <th>Importe aplicado</th>
                        <th>Importe total PP</th>
                        <th>Estado</th>
                        <th>Descripción</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>

                    <tbody>

                      {pagosProgramadosComprobante.map((p) => {

                        const marcadoParaDesasociar =
                          pagosProgramadosDesasociar.some(
                            (id) =>
                              Number(id) ===
                              Number(p.id)
                          );

                        const importeAplicado =
                          Number(
                            p.monto_aplicado ??
                            p.importe_aplicado ??
                            p.monto ??
                            0
                          );

                        const importeTotal =
                          Number(
                            p.monto || 0
                          );

                        const formaPago =
                          formasPagoTesoreria.find(
                            (fp) =>
                              Number(fp.id) ===
                              Number(p.formapago_id)
                          );

                        const formaPagoDescripcion =
                          formaPago?.descripcion ||
                          p.medio ||
                          "-";

                        return (

                          <tr
                            key={`programado-${p.id}`}
                            className={
                              marcadoParaDesasociar
                                ? "table-danger"
                                : ""
                            }
                          >

                            <td>
                              {p.fecha_programada || "-"}
                            </td>

                            <td>
                              #{p.id}
                            </td>

                            <td>
                              <strong>
                                {formaPagoDescripcion}
                              </strong>
                            </td>

                            <td>
                              <strong>
                                {fmtMoney(
                                  importeAplicado
                                )}
                              </strong>
                            </td>

                            <td>
                              {fmtMoney(
                                importeTotal
                              )}
                            </td>

                            <td>
                              {marcadoParaDesasociar ? (

                                <span className="badge bg-danger">
                                  SE DESASOCIARÁ AL GUARDAR
                                </span>

                              ) : (

                                <span className="badge bg-warning text-dark">
                                  PROGRAMADO · NO EFECTIVIZADO
                                </span>

                              )}
                            </td>

                            <td>

                              <Button
                                size="sm"
                                variant={
                                  marcadoParaDesasociar
                                    ? "outline-secondary"
                                    : "outline-danger"
                                }
                                onClick={() =>
                                  toggleDesasociarPagoProgramado(p)
                                }
                              >
                                {marcadoParaDesasociar
                                  ? "Deshacer"
                                  : "Desasociar"}
                              </Button>

                            </td>

                          </tr>

                        );

                      })}

                    </tbody>

                  </Table>
                )}

                <fieldset disabled={!puedeEditarComprobante}>

                  <hr />

                  <h5 className="mb-3">
                    Formas de pago aplicadas{" "}
                    {selectedComprobante?.ordenpago_id ? (
                      <small className="text-muted">
                        {" "}
                        (Orden #{selectedComprobante.ordenpago_id})
                      </small>
                    ) : null}
                  </h5>

                  {loadingPagos ? (

                    <div className="text-muted">
                      Cargando pagos…
                    </div>

                  ) : pagosComprobanteVisibles.length === 0 ? (

                    <div className="text-muted">
                      Este comprobante no tiene pagos efectivos asociados.
                    </div>

                  ) : (

                    <Table
                      striped
                      bordered
                      hover
                      size="sm"
                    >

                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Medio</th>
                          <th>Detalle</th>
                          <th>Importe</th>
                        </tr>
                      </thead>

                      <tbody>

                        {pagosComprobanteVisibles.map((p) => (

                          <tr key={`${p.tabla}-${p.id}`}>

                            <td>
                              {p.fecha || "-"}
                            </td>

                            <td>
                              {p.medio}
                            </td>

                            <td>
                              {p.detalle || "-"}
                            </td>

                            <td>
                              {fmtMoney(p.monto)}
                            </td>

                          </tr>

                        ))}

                      </tbody>

                    </Table>

                  )}

                </fieldset>
              </Form>
            </>


          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={guardandoEdicion}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleGuardarCambios}
            disabled={
              (
                !puedeEditarComprobante &&
                pagosProgramadosDesasociar.length === 0
              ) ||
              guardandoEdicion
            }
          >
            {guardandoEdicion
              ? "Guardando..."
              : "Guardar Cambios"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== Modal de creación ===== */}
      <Modal
        show={showCreateModal}
        onHide={handleCloseCreate}
        backdrop="static"
        centered
        size="lg"
        unmountOnClose
      >
        <Modal.Header closeButton>
          <Modal.Title>Ingresar Comprobante de Egreso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Tipo Comprobante</Form.Label>
                <Form.Select
                  name="tipocomprobante_id"
                  value={nuevoComprobante.tipocomprobante_id}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {tiposComprobanteTabla.map((tc) => (
                    <option key={tc.id} value={tc.id}>
                      {tc.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Punto de Venta</Form.Label>
                <Form.Select
                  name="ptoventa_id"
                  value={nuevoComprobante.ptoventa_id}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {ptosVtaFiltrados.map((pv) => (
                    <option key={pv.id} value={pv.id}>
                      {pv.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Libro IVA</Form.Label>
                <Form.Select
                  name="libroiva_id"
                  value={nuevoComprobante.libroiva_id || ""}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {librosIVAFiltrados.map((l) => (
                    <option key={l.id} value={l.id}>
                      {`${l.mes} / ${l.anio}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Fecha Comprobante</Form.Label>
                <Form.Control
                  type="date"
                  name="fechacomprobante"
                  value={nuevoComprobante.fechacomprobante}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Fecha Vencimiento</Form.Label>
                <Form.Control
                  type="date"
                  name="fechavencimiento"
                  value={nuevoComprobante.fechavencimiento}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Fecha Pago</Form.Label>
                <Form.Control
                  type="date"
                  name="fechapago"
                  value={nuevoComprobante.fechapago}
                  onChange={handleNuevoChange}
                />
              </Form.Group>
            </div>
            <div className="row">
              {/* Letra */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Letra</Form.Label>
                <Form.Control
                  name="letra"
                  value={nuevoComprobante.letra || ""}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              {/* N° Comprobante */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>N° Comprobante</Form.Label>
                <Form.Control
                  name="nrocomprobante"
                  value={nuevoComprobante.nrocomprobante}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              {/* Proveedor */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Proveedor</Form.Label>
                <Form.Select
                  name="proveedor_id"
                  value={nuevoComprobante.proveedor_id}
                  onChange={async (e) => {
                    handleNuevoChange(e);
                    const prov = e.target.value;
                    if (empresaSeleccionada?.id && prov) {
                      const ok = await checkHaciendaDisponibles({
                        empresaId: empresaSeleccionada.id,
                        proveedorId: prov,
                      });
                      setHasHaciendaDisponiblesCreate(ok);
                    } else {
                      setHasHaciendaDisponiblesCreate(false);
                    }
                  }}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {proveedoresOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </Form.Select>

                <div className="mt-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleOpenProveedorCreateModal}
                  >
                    + Nuevo proveedor
                  </Button>
                </div>
              </Form.Group>

              {/* Hacienda (ID + Buscar + Quitar todo junto) */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Hacienda (opcional)</Form.Label>
                <InputGroup>
                  <Form.Control
                    value={nuevoComprobante?.hacienda_id || ""}
                    placeholder="Sin seleccionar"
                    readOnly
                  />
                  <Button
                    variant="outline-primary"
                    disabled={
                      !empresaSeleccionada?.id ||
                      !nuevoComprobante?.proveedor_id ||
                      !hasHaciendaDisponiblesCreate
                    }
                    title={
                      !empresaSeleccionada?.id
                        ? "Seleccioná empresa"
                        : !nuevoComprobante?.proveedor_id
                          ? "Seleccioná proveedor"
                          : hasHaciendaDisponiblesCreate
                            ? "Buscar Hacienda disponibles"
                            : "Sin Hacienda disponibles"
                    }
                    onClick={() => setShowHaciendaPickerCreate(true)}
                    className="mx-1"
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outline-danger"
                    disabled={!nuevoComprobante?.hacienda_id}
                    title="Quitar Hacienda seleccionada"
                    onClick={() =>
                      setNuevoComprobante((prev) => ({ ...prev, hacienda_id: null }))
                    }
                  >
                    Quitar
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Doble click en una fila del modal para seleccionar.
                </Form.Text>
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Categoría de Egreso (encabezado)</Form.Label>
                <Form.Select
                  name="categoriaegreso_id"
                  value={nuevoComprobante.categoriaegreso_id || ""}
                  onChange={(e) => {
                    handleNuevoChange(e);
                    const imp = getImputacionFromCategoria(e.target.value);
                    setNuevoComprobante((prev) => ({
                      ...prev,
                      imputacioncontable_id: imp || "",
                    }));
                  }}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {categoriasEgreso.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </option>
                  ))}
                </Form.Select>
                <small className="text-muted">
                  Esta categoría se usará por defecto en las formas de pago.
                </small>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Imputación Contable (encabezado)</Form.Label>
                <Form.Control
                  value={
                    imputacionContableTabla.find(
                      (x) => Number(x.id) === Number(nuevoComprobante.imputacioncontable_id)
                    )?.descripcion || ""
                  }
                  placeholder="Se completa según la categoría"
                  disabled
                />
              </Form.Group>
            </div>

            <div className="row">

              {/* NETO */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Neto</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="neto"
                  value={nuevoComprobante.neto ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* IVA 10,5% */}
              <Form.Group className="mb-3 col-md-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="mb-0">IVA 10,5%</Form.Label>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={calcIVA105Create}
                  >
                    calc
                  </Button>
                </div>

                <Form.Control
                  type="number"
                  step="0.01"
                  name="iva105"
                  value={nuevoComprobante.iva105 ?? 0}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              {/* IVA 21% */}
              <Form.Group className="mb-3 col-md-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="mb-0">IVA 21%</Form.Label>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={calcIVA21Create}
                  >
                    calc
                  </Button>
                </div>

                <Form.Control
                  type="number"
                  step="0.01"
                  name="iva21"
                  value={nuevoComprobante.iva21 ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* IVA ESPECIAL % */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>IVA especial %</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="iva_especial_porcentaje"
                  value={nuevoComprobante.iva_especial_porcentaje ?? ""}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              {/* IVA ESPECIAL IMPORTE */}
              <Form.Group className="mb-3 col-md-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="mb-0">IVA especial</Form.Label>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={calcIVAEspecialCreate}
                  >
                    calc
                  </Button>
                </div>

                <Form.Control
                  type="number"
                  step="0.01"
                  name="iva_especial"
                  value={nuevoComprobante.iva_especial ?? 0}
                  onChange={handleNuevoChange}
                />
              </Form.Group>

              {/* PERCEPCIÓN IVA */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Percep. IVA</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="percepcion_iva"
                  value={nuevoComprobante.percepcion_iva ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* PERCEPCIÓN GANANCIAS */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Percep. Ganancias</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="percepcion_ganancias"
                  value={nuevoComprobante.percepcion_ganancias ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* PERCEPCIÓN IIBB */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Percep. IIBB</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="percepcion_iibb"
                  value={nuevoComprobante.percepcion_iibb ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* IMPUESTOS INTERNOS */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Imp. Internos</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="impuestos_internos"
                  value={nuevoComprobante.impuestos_internos ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* EXENTO / NO GRAVADO */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Exento / No Grav.</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="exento_no_gravado"
                  value={nuevoComprobante.exento_no_gravado ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* RETENCIÓN */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Retención</Form.Label>

                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  name="retencion"
                  value={nuevoComprobante.retencion ?? 0}
                  onChange={handleNuevoChange}
                  onFocus={handleFocusNumero}
                />
              </Form.Group>

              {/* TOTAL */}
              <Form.Group className="mb-3 col-md-3">
                <Form.Label>Total (auto)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="total"
                  value={nuevoComprobante.total ?? 0}
                  readOnly
                />
              </Form.Group>

            </div>


            {/* ===== Ajustes del comprobante ===== */}
            <div className="border rounded p-3 mb-3">

              <div className="d-flex justify-content-between align-items-center mb-3">

                <div>
                  <h5 className="mb-0">
                    Ajustes
                  </h5>

                  <small className="text-muted">
                    Modifican el importe financiero a pagar sin modificar el total fiscal del comprobante.
                  </small>
                </div>

                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={agregarAjuste}
                >
                  + Agregar ajuste
                </Button>

              </div>

              {ajustes.length === 0 ? (

                <div className="text-muted">
                  No hay ajustes aplicados.
                </div>

              ) : (

                ajustes.map((ajuste, idx) => (

                  <div
                    key={idx}
                    className="border rounded p-2 mb-2"
                  >

                    <div className="row">

                      <Form.Group className="mb-2 col-md-3">

                        <Form.Label>
                          Tipo
                        </Form.Label>

                        <Form.Select
                          value={ajuste.tipo}
                          onChange={(e) =>
                            actualizarAjuste(
                              idx,
                              "tipo",
                              e.target.value
                            )
                          }
                        >

                          <option value="disminuye">
                            Disminuye
                          </option>

                          <option value="aumenta">
                            Aumenta
                          </option>

                        </Form.Select>

                      </Form.Group>


                      <Form.Group className="mb-2 col-md-3">

                        <Form.Label>
                          Importe
                        </Form.Label>

                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={ajuste.importe}
                          onChange={(e) =>
                            actualizarAjuste(
                              idx,
                              "importe",
                              e.target.value
                            )
                          }
                        />

                      </Form.Group>


                      <Form.Group className="mb-2 col-md-6">

                        <Form.Label>
                          Concepto
                        </Form.Label>

                        <Form.Control
                          value={ajuste.concepto}
                          onChange={(e) =>
                            actualizarAjuste(
                              idx,
                              "concepto",
                              e.target.value
                            )
                          }
                          placeholder="Ej.: diferencia, redondeo, descuento..."
                        />

                      </Form.Group>

                    </div>


                    <div className="row">

                      <Form.Group className="mb-2 col-md-9">

                        <Form.Label>
                          Detalle
                        </Form.Label>

                        <Form.Control
                          value={ajuste.detalle}
                          onChange={(e) =>
                            actualizarAjuste(
                              idx,
                              "detalle",
                              e.target.value
                            )
                          }
                        />

                      </Form.Group>


                      <div className="col-md-3 d-flex align-items-end justify-content-end mb-2">

                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() =>
                            eliminarAjuste(idx)
                          }
                        >
                          Eliminar
                        </Button>

                      </div>

                    </div>

                  </div>

                ))

              )}


              <div className="mt-3 text-end">

                <div>
                  Total base:{" "}
                  <strong>
                    {fmtMoney(totalBaseFinanciero)}
                  </strong>
                </div>

                <div>
                  Ajustes aumenta:{" "}
                  <strong>
                    + {fmtMoney(totalAjustesAumenta)}
                  </strong>
                </div>

                <div>
                  Ajustes disminuye:{" "}
                  <strong>
                    - {fmtMoney(totalAjustesDisminuye)}
                  </strong>
                </div>

                <div className="mt-1">
                  Total financiero:{" "}
                  <strong>
                    {fmtMoney(totalParaPagos)}
                  </strong>
                </div>

              </div>

            </div>


            {/* Editor de formas de pago (con existing_ref y gasto mensual) */}
            <FormasPagoEditor
              total={totalParaPagos}           // **base de pagos**: Monto real si LCD, Total si no
              value={pagos}
              onChange={setPagos}
              headerCategoriaId={nuevoComprobante.categoriaegreso_id || null}
              headerImputacionId={nuevoComprobante.imputacioncontable_id || null}
              showCategoriaPorFila={false}
              empresaId={empresaSeleccionada?.id || null}
              proveedorId={nuevoComprobante.proveedor_id || null}
              fechaComprobante={nuevoComprobante.fechacomprobante}   // ⬅️ IMPORTANTE
            />

            <div className="row mt-2">
              {/* Estado Pago */}
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Estado Pago</Form.Label>
                <Form.Select
                  name="estadopago"
                  value={nuevoComprobante.estadopago}
                  onChange={handleNuevoChange}
                  className="form-control my-input"
                >
                  <option value="pagada">Pagada</option>
                  <option value="impaga">Impaga</option>
                  <option value="parcial">Parcial</option>
                </Form.Select>
              </Form.Group>

              {/* Monto Real (solo editable en LCD) */}
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Monto Real {esLCDCreate ? "" : "(solo LCD)"}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="montoreal"
                  value={esLCDCreate ? (nuevoComprobante.montoreal || 0) : 0}
                  onChange={esLCDCreate ? handleNuevoChange : undefined}
                  onFocus={esLCDCreate ? handleFocusNumero : undefined}
                  readOnly={!esLCDCreate}
                />
              </Form.Group>

              {/* Diferencia Efectivo (auto) */}
              <Form.Group className="mb-3 col-md-4">
                <Form.Label>Diferencia Efectivo (auto)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="diferenciaefectivo"
                  value={esLCDCreate ? (nuevoComprobante.diferenciaefectivo || 0) : 0}
                  readOnly
                />
                <small className="text-muted">
                  {esLCDCreate ? "Se calcula: Monto real − Total." : "Disponible solo para LCD."}
                </small>
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                as="textarea"
                name="observaciones"
                rows={2}
                value={nuevoComprobante.observaciones}
                onChange={handleNuevoChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div className="me-auto text-muted">
            Suma pagos: <strong>{fmtMoney(sumaPagos)}</strong>{" "}
            {montosOk ? "✓" : esLCDCreate ? "↔️ debe coincidir con Monto real" : "↔️ debe coincidir con Total"}
          </div>
          <Button
            variant="secondary"
            onClick={handleCloseCreate}
            disabled={creandoComprobante}
          >
            Cancelar
          </Button>

          <Button
            variant="success"
            onClick={handleCrearComprobante}
            disabled={
              !montosOk ||
              !empresaSeleccionada ||
              creandoComprobante
            }
          >
            {creandoComprobante
              ? "Creando..."
              : "Crear Comprobante"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showProveedorCreateModal}
        onHide={handleCloseProveedorCreateModal}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Nuevo Proveedor</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  name="nombre"
                  value={nuevoProveedorModal.nombre}
                  onChange={handleNuevoProveedorModalChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Teléfono</Form.Label>
                <Form.Control
                  name="telefono"
                  value={nuevoProveedorModal.telefono}
                  onChange={handleNuevoProveedorModalChange}
                />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  name="email"
                  value={nuevoProveedorModal.email}
                  onChange={handleNuevoProveedorModalChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  name="direccion"
                  value={nuevoProveedorModal.direccion}
                  onChange={handleNuevoProveedorModalChange}
                />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>CUIT</Form.Label>
                <Form.Control
                  name="cuit"
                  value={nuevoProveedorModal.cuit}
                  onChange={handleNuevoProveedorModalChange}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>DNI</Form.Label>
                <Form.Control
                  name="dni"
                  value={nuevoProveedorModal.dni}
                  onChange={handleNuevoProveedorModalChange}
                />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Imputación Contable</Form.Label>
                <Form.Select
                  name="imputacioncontable_id"
                  value={nuevoProveedorModal.imputacioncontable_id}
                  onChange={handleNuevoProveedorModalChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {imputacionContableTabla.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Forma de Pago</Form.Label>
                <Form.Select
                  name="formapago_id"
                  value={nuevoProveedorModal.formapago_id}
                  onChange={handleNuevoProveedorModalChange}
                  className="form-control my-input"
                >
                  <option value="">Seleccione...</option>
                  {formasPagoTesoreria.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {errorCrearProveedor && (
              <div className="alert alert-danger py-2 mb-0">
                {errorCrearProveedor}
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseProveedorCreateModal}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleCrearProveedorDesdeComprobante}
            disabled={loadingCrearProveedor}
          >
            {loadingCrearProveedor ? "Creando..." : "Crear Proveedor"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* [REF: HAC_MODAL_RENDER] Modal Picker - Crear */}
      <HaciendaPickerModal
        show={showHaciendaPickerCreate}
        onHide={() => setShowHaciendaPickerCreate(false)}
        empresaId={empresaSeleccionada?.id || null}
        proveedorId={nuevoComprobante?.proveedor_id || null}
        onSelect={(h) => {
          setNuevoComprobante((prev) => ({ ...prev, hacienda_id: h.id }));
          setShowHaciendaPickerCreate(false);
        }}
      />

      {/* [REF: HAC_MODAL_RENDER] Modal Picker - Editar */}
      <HaciendaPickerModal
        show={showHaciendaPickerEdit}
        onHide={() => setShowHaciendaPickerEdit(false)}
        empresaId={empresaSeleccionada?.id || null}
        proveedorId={selectedComprobante?.proveedor_id || null}
        comprobanteId={selectedComprobante?.id || null}  // <— pásalo al modal
        onSelect={(h) => {
          // solo estado local; el update real se hace al Guardar
          setSelectedComprobante((prev) => ({ ...prev, hacienda_id: h.id }));
          setShowHaciendaPickerEdit(false);
        }}
      />



    </Container >
  );
}
